import React, { useEffect } from 'react'
import { unstable_HistoryRouter as HistoryRouter, Navigate, Outlet, Route, Routes as RoutesWrapper } from 'react-router'

import { useCompanyFeatureFlags } from '@/app-config/featureFlags'
import { CheckFeaturePermission } from '@/atoms/CheckFeaturePermission'
import CheckPermission from '@/atoms/CheckUserPermission/CheckUserPermission'
import DoLogout from '@/atoms/DoLogout/DoLogout'
import { ModalApiProvider } from '@/atoms/ModalApi'
import { useIsLoggedIn } from '@/atoms/PrivateRoute/useIsLoggedIn'
import { TabPanel } from '@/atoms/Tabs/TabPanel'
import { AhrefSubTabs } from '@/atoms/Tabs/Tabs'
import history from '@/lib/history'
import { useTranslation } from '@/lib/i18n/i18n'
import { Routes } from '@/lib/routes'
import { NotFoundBanner } from '@/molecules/NotFoundBanner'
import { RestrictedMobileAppRoute } from '@/molecules/RestrictedMobileAppContent/RestrictedMobileAppRoute'
import {
	ActivateInvoiceProfile,
	BookConsultant,
	CallbackVerification,
	ChangePlan,
	CompanyPolicySettings,
	ConfirmCompanyPage,
	CorporateDiscountsAndRewardPrograms,
	CreateCompanyInvoices,
	CreateInvoiceProfileWizard,
	CreateTravelerProfile,
	CreditCardCallbackVerification,
	CurrentPlan,
	EditDailyAllowance,
	EditDailyAllowanceInReport,
	EditExpenseReport,
	EditMileage,
	EditMileageInReport,
	EditStandardExpense,
	EditStandardExpenseInReport,
	EditTraveler,
	ExpensePolicySettings,
	ExpenseReports,
	ExpenseReportsSubmitted,
	GeneralSettings,
	HomePage,
	HotelResultPage,
	InvoiceProfiles,
	Itinerary,
	ManageDepartments,
	ManageExpenses,
	ManageLocations,
	ManagePolicy,
	ManageSyncSSOConfig,
	ManageTravelers,
	NewDailyAllowance,
	NewDailyAllowanceInReport,
	NewExpenseReport,
	NewMileage,
	NewMileageInReport,
	NewStandardExpense,
	NewStandardExpenseInReport,
	OfficePolicySettings,
	PasswordChangeResult,
	PolicyGroupSettings,
	ReportSettings,
	ReportsRouter,
	Safety,
	SelectExpensesInReport,
	Support,
	TravelerProfileRoutes,
	TravelerProfileUnpaidFee,
	TravelerUnusedTickets,
	TripBuilder,
	TripProduct,
	TripProductSearch,
	TripsList,
	UsageDetails,
} from '@/pages/App/lazyRoutes'
import { PageLayout } from '@/pages/App/PageLayout'
import { GeneralErrorPage } from '@/pages/GeneralErrorPage'
import LoginWithSeo from '@/pages/Login'
import { OrderCallbackVerification } from '@/pages/OrderCallbackVerification/OrderCallbackVerification'
import { PageContainer } from '@/pages/Page/Page'
import { TripProductBase } from '@/pages/TripBuilder/TripProductBase'
import { useIdentifyUser } from '@/tracking/useIdentifyUser'
import { useUserTracking } from '@/tracking/useUserTracking'
import { trackEvent } from '@/travelsuit/analytics'
import { FeatureScope } from '@/types/billing'

const AppRouter = () => {
	const userTrackingClient = useUserTracking()

	useEffect(
		() =>
			history.listen(() => {
				trackEvent({ action: 'Page View' })
				userTrackingClient?.reload()
			}),
		[userTrackingClient],
	)

	useIdentifyUser()

	return (
		<HistoryRouter
			history={history as unknown as React.ComponentProps<typeof HistoryRouter>['history']}
			unstable_useTransitions
		>
			<RoutesWrapper>
				<Route Component={Root}>
					<Route Component={PageLayout} errorElement={<GeneralErrorPage errorId="" />}>
						<Route path={Routes.LiveMap} Component={() => <Navigate to={Routes.Home} />} />
						<Route path={Routes.NewTrip} Component={() => <Navigate to={Routes.Home} />} />
						<Route path={Routes.Home} Component={HomePage} />
						<Route path={Routes.Safety} Component={Safety} />
						<Route path={Routes.Admin} Component={AdminContainer}>
							<Route path={Routes.ReportSettings} Component={ReportSettings} />
							<Route
								path={Routes.CorporateDiscountsAndRewardPrograms}
								Component={CorporateDiscountsAndRewardPrograms}
							/>
							<Route Component={AdminTabs}>
								<Route path={Routes.GeneralSettings} Component={GeneralSettings} />
								<Route path={Routes.ManageTravelers} Component={ManageTravelers} />
								<Route path={Routes.ManageDepartments} Component={ManageDepartments} />
								<Route path={Routes.ManageLocations} Component={ManageLocations} />
								<Route path={Routes.ManageSso} Component={ManageSyncSSOConfig} />
							</Route>
						</Route>
						<Route Component={ManagePolicyContainer}>
							<Route path={Routes.CompanyPolicySettings} Component={CompanyPolicySettings} />
							<Route path={Routes.ExpensePolicySettings} Component={ExpensePolicySettings} />
							<Route path={Routes.OfficePolicySettings} Component={OfficePolicySettings} />
							<Route path={Routes.PolicyGroupSettings} Component={PolicyGroupSettings} />
						</Route>
						<Route path={Routes.Reports} Component={ReportsContainer}>
							<Route path="*" Component={ReportsRouter} />
						</Route>
						<Route path="/trips">
							<Route path={Routes.NewSearch} Component={HomePage} />
							<Route path={Routes.Itinerary} Component={Itinerary} />
							<Route path={Routes.HotelResult} Component={HotelResultPage} />
							<Route path={Routes.TripBuilder} Component={TripBuilder} />
							<Route path={Routes.Trips} Component={TripsList} />
							<Route Component={TripProductBase}>
								<Route path={Routes.TripProduct} Component={TripProduct} />
								<Route path={Routes.TripProductSearch} Component={TripProductSearch} />
								<Route path={Routes.TripProductSearchWithStep} Component={TripProductSearch} />
							</Route>
							<Route path="*" Component={NotFoundBanner} />
						</Route>
						<Route Component={ExpensesContainer}>
							<Route path={Routes.NewStandardExpense} Component={NewStandardExpense} />
							<Route path={Routes.NewStandardExpenseInReport} Component={NewStandardExpenseInReport} />
							<Route path={Routes.EditStandardExpense} Component={EditStandardExpense} />
							<Route path={Routes.EditStandardExpenseInReport} Component={EditStandardExpenseInReport} />
							<Route path={Routes.EditMileageInReport} Component={EditMileageInReport} />
							<Route path={Routes.EditDailyAllowanceInReport} Component={EditDailyAllowanceInReport} />
							<Route path={Routes.NewExpenseReport} Component={NewExpenseReport} />
							<Route path={Routes.EditExpenseReport} Component={EditExpenseReport} />
							<Route path={Routes.ExpenseReportsSubmitted} Component={ExpenseReportsSubmitted} />
							<Route path={Routes.SelectExpensesInReport} Component={SelectExpensesInReport} />
							<Route path={Routes.NewMileage} Component={NewMileage} />
							<Route path={Routes.NewMileageInReport} Component={NewMileageInReport} />
							<Route path={Routes.EditMileage} Component={EditMileage} />
							<Route path={Routes.NewDailyAllowance} Component={NewDailyAllowance} />
							<Route path={Routes.NewDailyAllowanceInReport} Component={NewDailyAllowanceInReport} />
							<Route path={Routes.EditDailyAllowance} Component={EditDailyAllowance} />
							<Route Component={ExpensesTabs}>
								<Route path={Routes.ManageExpenses} Component={ManageExpenses} />
								<Route path={Routes.ExpenseReports} Component={ExpenseReports} />
							</Route>
						</Route>
						<Route path={Routes.CreateTravelerProfile} Component={CreateTravelerProfile} />
						<Route Component={TravelerProfileRoutes}>
							<Route path={Routes.EditTravelerProfile} Component={EditTraveler} />
							<Route path={Routes.TravelerProfileUnusedTickets} Component={TravelerUnusedTickets} />
							<Route path={Routes.TravelerProfileUnpaidFee} Component={TravelerProfileUnpaidFee} />
						</Route>
						<Route path={Routes.CallbackVerification} Component={CallbackVerification} />
						<Route path={Routes.CreditCardCallbackVerification} Component={CreditCardCallbackVerification} />
						<Route path={Routes.WordlineOrderCallbackVerification} Component={OrderCallbackVerification} />
						<Route path={Routes.BookCustomerSuccessConsultant} Component={BookConsultant} />
						<Route path={Routes.Support} Component={Support} />
						<Route Component={RestrictedMobileAppRoute}>
							<Route Component={BillingContainer}>
								<Route path={Routes.Billing} Component={InvoiceProfiles} />
								<Route path={Routes.BillingCurrent} Component={CurrentPlan} />
								<Route path={Routes.BillingUsage} Component={UsageDetails} />
							</Route>
							<Route Component={AdminContainer}>
								<Route path={Routes.CreateInvoiceProfile} Component={CreateInvoiceProfileWizard} />
								<Route path={Routes.BillingChangePlan} Component={ChangePlan} />
								<Route path={Routes.ActivateInvoiceProfile} Component={ActivateInvoiceProfile} />
							</Route>
							<Route path={Routes.CreateCompanyInvoices} Component={CreateCompanyInvoices} />
							<Route path={Routes.OnboardingConfirmation} Component={ConfirmCompanyPage} />
						</Route>
					</Route>
					<Route Component={LoginContainer}>
						<Route path={Routes.Login} Component={LoginWithSeo} />
						<Route path={Routes.Logout} Component={DoLogout} />
						<Route path={Routes.PasswordChangeResult} Component={PasswordChangeResult} />,
					</Route>
					<Route path="*" element={<NotFoundBanner />} />
				</Route>
			</RoutesWrapper>
		</HistoryRouter>
	)
}

export default AppRouter

function Root() {
	return (
		<ModalApiProvider>
			<Outlet />
		</ModalApiProvider>
	)
}

function AdminTabs() {
	const { t } = useTranslation()
	const is_sso_flag_enabled = Boolean(useCompanyFeatureFlags()?.enable_sso_ui || false)

	return (
		<PageContainer>
			<AhrefSubTabs id="admin" data-test="AppRouter.AdminSubmenu">
				{[
					{
						href: Routes.GeneralSettings,
						label: t('app-router.admin.general'),
					},
					{
						href: Routes.ManageTravelers,
						label: t('app-router.admin.users'),
						'data-test': 'TravelersTabElement',
					},
					{
						href: Routes.ManageDepartments,
						label: t('app-router.admin.departments'),
					},
					{ href: Routes.ManageLocations, label: t('app-router.admin.locations') },
					...(is_sso_flag_enabled
						? [
								{
									href: Routes.ManageSso,
									label: t('SSO'),
								},
							]
						: []),
				]}
			</AhrefSubTabs>
			<TabPanel id="admin">
				<Outlet />
			</TabPanel>
		</PageContainer>
	)
}

function AdminContainer() {
	return (
		<CheckPermission permissions={['Admin']} fallback={<NotFoundBanner />}>
			<Outlet />
		</CheckPermission>
	)
}

function ReportsContainer() {
	return (
		<CheckPermission permissions={['Admin', 'ViewAllReports', 'ViewManagedGroupReports']} fallback={<NotFoundBanner />}>
			<Outlet />
		</CheckPermission>
	)
}

function ManagePolicyContainer() {
	return (
		<CheckPermission permissions={['Admin']} fallback={<NotFoundBanner />}>
			<ManagePolicy />
		</CheckPermission>
	)
}

function LoginContainer() {
	return useIsLoggedIn() ? <Navigate to={Routes.Home} /> : <Outlet />
}

function BillingContainer() {
	return (
		<CheckPermission permissions={['Admin']} fallback={<NotFoundBanner />}>
			<BillingTabs />
		</CheckPermission>
	)
}

function BillingTabs() {
	const { t } = useTranslation()

	return (
		<PageContainer>
			<AhrefSubTabs id="billing" data-test="AppRouter.BillingSubmenu">
				{[
					{
						href: Routes.Billing,
						label: t('app-router.billing.invoice-profiles'),
					},
					{
						href: Routes.BillingCurrent,
						label: t('app-router.billing.plans'),
					},
					{ href: Routes.BillingUsage, label: t('app-router.billing.usage') },
				]}
			</AhrefSubTabs>
			<TabPanel id="billing">
				<Outlet />
			</TabPanel>
		</PageContainer>
	)
}

function ExpensesContainer() {
	return (
		<CheckFeaturePermission requiredFeature={FeatureScope.TravelExpense}>
			{({ isLoading, isPermitted }) => {
				if (isLoading) {
					return <></>
				}

				if (!isPermitted) {
					return <NotFoundBanner />
				}

				return <Outlet />
			}}
		</CheckFeaturePermission>
	)
}

function ExpensesTabs() {
	const { t } = useTranslation()

	return (
		<PageContainer>
			<AhrefSubTabs id="expenses">
				{[
					{
						href: Routes.ManageExpenses,
						label: t('app-router.expenses.manage'),
					},
					{
						href: Routes.ExpenseReports,
						label: t('app-router.expenses.reports'),
					},
				]}
			</AhrefSubTabs>
			<TabPanel id="expenses">
				<Outlet />
			</TabPanel>
		</PageContainer>
	)
}
